import React, { useEffect, useState } from 'react'
import { Container, YStack, Spinner, XStack, Spacer, Button, useDebounceValue } from '@my/ui'
import { MainLayout, Meta, SectionTitle } from 'app/components'
import { FilterOption, GroupNoResults, GroupSearch, SortOption } from '../../components'
import { SearchGroupsQueryOutput, SearchGroupsSort } from './queries'
import { User } from 'app/types'
import {
  GroupCategoriesOutput,
  UserGroupJoinRequestsQueryOutput,
  UserGroupsQueryOutput,
} from '../../queries'
import { FlatList } from 'react-native'
import { GroupCardHorizontal } from '../../containers'
import {
  canJoinGroup,
  canLeaveGroup,
  hasJoinedGroup,
  hasPendingGroupMembershipRequest,
} from '../../helpers'

export interface GroupsDiscoverScreenProps {
  HeaderComponent?: React.ReactNode
  user: User | null
  groupCategories: GroupCategoriesOutput['groupCategories']['docs']
  userGroupJoinRequests: UserGroupJoinRequestsQueryOutput['userGroupJoinRequests']['docs']
  groups: SearchGroupsQueryOutput['searchGroups']['docs']
  userGroups: UserGroupsQueryOutput['userGroups']['docs']
  totalResults: number
  onSearchChange: (query: string, filters: FilterOption[], sort?: SearchGroupsSort) => void

  searchLoading: boolean
  searchFetchingMore: boolean
  hasMore: boolean
  fetchMore: () => void

  refreshing: boolean
  onPullToRefresh: () => void
}

const sortOptions = [
  {
    label: 'Name (A-Z)',
    value: SearchGroupsSort.NameAsc,
  },
  {
    label: 'Name (Z-A)',
    value: SearchGroupsSort.NameDesc,
  },
  {
    label: 'Latest',
    value: SearchGroupsSort.DateDesc,
  },
  {
    label: 'Oldest',
    value: SearchGroupsSort.DateAsc,
  },
  {
    label: 'Most Members',
    value: SearchGroupsSort.MembersDesc,
  },
]

export const GroupsDiscoverScreen = (props: GroupsDiscoverScreenProps) => {
  const [activeFilters, setActiveFilters] = useState<FilterOption[]>([])
  const [activeSortOption, setActiveSortOption] = useState<null | SortOption>(null)
  const [query, setQuery] = useState('')

  const debouncedSearchTerm = useDebounceValue(query, 350)

  const handleSortChange = (item: SortOption) => {
    if (item === activeSortOption) {
      setActiveSortOption(null)
    } else {
      setActiveSortOption(item)
    }
  }

  const handleFilterChange = (filter: FilterOption) => {
    let newFilters: FilterOption[] = []
    if (activeFilters.find((f) => f.value === filter.value)) {
      newFilters = activeFilters.filter((f) => f.value !== filter.value)
    } else {
      newFilters = [...activeFilters, filter]
    }

    setActiveFilters(newFilters)
  }

  // Trigger search when search params change
  useEffect(() => {
    props.onSearchChange(
      debouncedSearchTerm,
      activeFilters,
      activeSortOption?.value as SearchGroupsSort
    )
  }, [debouncedSearchTerm, activeSortOption, activeFilters])

  const groupFilters =
    props.groupCategories?.map((group) => ({
      label: group.name,
      value: group.id,
    })) || []

  const noResults = !props.searchLoading && props.groups.length === 0

  return (
    <MainLayout>
      <Meta title="Discover Groups" />
      {props.HeaderComponent && props.HeaderComponent}
      <YStack
        bg="$background"
        flexGrow={1}
        gap="$4"
        $gtMd={{
          gap: '$6',
        }}
      >
        <FlatList
          data={props.groups}
          keyExtractor={(item) => item.id}
          onRefresh={props.onPullToRefresh}
          refreshing={props.refreshing}
          renderItem={({ item }) => {
            const { isAdmin, isMember } = hasJoinedGroup({
              userGroups: props.userGroups,
              groupId: item.id,
            })
            const pendingRequest = hasPendingGroupMembershipRequest({
              groupJoinRequests: props.userGroupJoinRequests,
              groupId: item.id,
            })
            return (
              <Container jc="flex-start" key={item.id} size="sm" gutter>
                <GroupCardHorizontal
                  id={item.id}
                  name={item.name}
                  visibility={item.visibility}
                  totalMembers={item?.totalMembers || 0}
                  href={`/groups/${item.id}`}
                  canJoin={!!props.user && canJoinGroup({ group: item, user: props.user })}
                  hasJoined={isMember}
                  hasPendingRequest={pendingRequest}
                  isAdmin={isAdmin}
                  canLeave={isMember && !isAdmin && canLeaveGroup({ group: item })}
                  profileImage={{
                    url: item.profileImage?.sizes.small.url,
                    alt: item.name,
                  }}
                />
              </Container>
            )
          }}
          ListHeaderComponent={
            <Container
              gutter
              size="sm"
              gap="$4"
              spaceTop
              pb="$3.5"
              $gtMd={{
                gap: '$6',
              }}
            >
              <GroupSearch
                w="100%"
                totalResults={props.totalResults}
                filters={groupFilters}
                activeFilters={activeFilters}
                query={query}
                onFilterChange={handleFilterChange}
                onSearchChange={setQuery}
                sortOptions={sortOptions}
                onSortChange={handleSortChange}
                activeSortOption={activeSortOption}
              />

              {!noResults && (
                <SectionTitle>{query.length > 2 ? 'Results' : 'Discover groups'}</SectionTitle>
              )}

              {noResults && (
                <GroupNoResults
                  title="Sorry, there’s no Groups related to your search"
                  description="But don’t stop here – Try less specific search terms and check the spelling."
                />
              )}
            </Container>
          }
          ItemSeparatorComponent={() => <Spacer size="$3.5" />}
          ListFooterComponent={
            <Container gutter size="sm" gap="$4" pt="$6" spaceBottom>
              {props.searchLoading && (
                <XStack w="100%" jc="center" ai="center">
                  <Spinner size="large" color="$black" />
                </XStack>
              )}

              {props.hasMore && (
                <XStack w="100%" jc="center" ai="center">
                  <Button
                    onPress={props.fetchMore}
                    theme="primary"
                    loading={props.searchFetchingMore}
                  >
                    Load more
                  </Button>
                </XStack>
              )}
            </Container>
          }
        />
      </YStack>
    </MainLayout>
  )
}
